import {
  CanadaTaxJurisdictionEnum,
  CountryCode,
  EU_AGGREGATOR_COUNTRY_CODE,
  EU_AGGREGATOR_STATE_LABEL,
  EUCountryEnum,
} from '@/types/location';
import { EU_COUNTRY_LABELS, USStates } from './app-constants';

export const US_JURISDICTION_CODES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'NE',
  'NV',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'PR',
];

export const CANADIAN_TAX_JURISDICTIONS = Object.entries(CanadaTaxJurisdictionEnum).map(([key, value]) => {
  const jurisdictionLabels: Record<string, string> = {
    BRITISH_COLUMBIA: 'British Columbia',
    FEDERAL: 'Federal',
    MANITOBA: 'Manitoba',
    QUEBEC: 'Quebec',
    SASKATCHEWAN: 'Saskatchewan',
  };

  return {
    value: value,
    label: jurisdictionLabels[key] || key,
  };
});

export const EU_TAX_JURISDICTIONS = [
  // EU Aggregator option
  { value: EU_AGGREGATOR_COUNTRY_CODE, label: EU_AGGREGATOR_STATE_LABEL },
  // Individual EU member countries
  ...Object.values(EUCountryEnum).map(countryCode => ({
    value: countryCode,
    label: EU_COUNTRY_LABELS[countryCode],
  })),
];

const AUSTRALIA_TAX_JURISDICTIONS = [{ value: CountryCode.AU, label: 'Australia' }];

const NEW_ZEALAND_TAX_JURISDICTIONS = [{ value: CountryCode.NZ, label: 'New Zealand' }];

const MEXICO_TAX_JURISDICTIONS = [{ value: CountryCode.MX, label: 'Mexico' }];

const SAUDI_ARABIA_TAX_JURISDICTIONS = [{ value: CountryCode.SA, label: 'Saudi Arabia' }];

const SINGAPORE_TAX_JURISDICTIONS = [{ value: CountryCode.SG, label: 'Singapore' }];

const UAE_TAX_JURISDICTIONS = [{ value: CountryCode.AE, label: 'United Arab Emirates' }];

// Switzerland is geographically European but not an EU member (not part of EUCountryEnum),
// so it needs its own federal jurisdiction entry, matching Australia/New Zealand/Mexico/etc.
const SWITZERLAND_TAX_JURISDICTIONS = [{ value: CountryCode.CH, label: 'Switzerland' }];

// 'ZZ_IN' is a synthetic filter code for India, mirroring EU_AGGREGATOR_COUNTRY_CODE = 'ZZ_EU'.
// India's ISO code 'IN' collides with Indiana's US state code, so the filter context uses
// this synthetic code instead. applyJurisdictionFilter normalizes it back to 'IN' for API calls.
export const INDIA_JURISDICTION_FILTER_CODE = 'ZZ_IN';

const INDIA_TAX_JURISDICTIONS = [{ value: INDIA_JURISDICTION_FILTER_CODE, label: 'India' }];

export const getAllUSJurisdictions = () => {
  return US_JURISDICTION_CODES.map(code => {
    const state = USStates.find(s => s.value === code);
    return {
      value: code,
      label: state?.label || code,
    };
  }).sort((a, b) => a.label.localeCompare(b.label));
};

export const getJurisdictionsForCountryCode = (countryCode: string) => {
  switch (countryCode) {
    case CountryCode.US:
      return getAllUSJurisdictions();
    case CountryCode.CA:
      return CANADIAN_TAX_JURISDICTIONS;
    case CountryCode.AU:
      return AUSTRALIA_TAX_JURISDICTIONS;
    case CountryCode.NZ:
      return NEW_ZEALAND_TAX_JURISDICTIONS;
    case CountryCode.MX:
      return MEXICO_TAX_JURISDICTIONS;
    case CountryCode.SA:
      return SAUDI_ARABIA_TAX_JURISDICTIONS;
    case CountryCode.SG:
      return SINGAPORE_TAX_JURISDICTIONS;
    case CountryCode.AE:
      return UAE_TAX_JURISDICTIONS;
    case CountryCode.CH:
      return SWITZERLAND_TAX_JURISDICTIONS;
    case CountryCode.IN:
      return INDIA_TAX_JURISDICTIONS;
    case EU_AGGREGATOR_COUNTRY_CODE:
      return [{ value: EU_AGGREGATOR_COUNTRY_CODE, label: EU_AGGREGATOR_STATE_LABEL }];
    default:
      if (Object.values(EUCountryEnum).includes(countryCode as EUCountryEnum)) {
        return [{ value: countryCode, label: EU_COUNTRY_LABELS[countryCode as EUCountryEnum] || countryCode }];
      }
      return [];
  }
};

// Region buckets mirror getGroupedRegistrationCountries (utils/address.ts): US, Canada, and
// Europe stay standalone; every other country is grouped by continent/region.
export const ALL_JURISDICTIONS_BY_REGION = [
  {
    label: '',
    options: [{ value: 'all', label: 'All Jurisdictions' }],
  },
  {
    // 'US' aggregator selects every US state at once (maps to country_code=US via
    // applyJurisdictionFilter). Listed first so it sits above the individual states.
    label: 'United States',
    options: [{ value: CountryCode.US, label: 'All US States' }, ...getAllUSJurisdictions()],
  },
  {
    label: 'Canada',
    options: CANADIAN_TAX_JURISDICTIONS,
  },
  {
    // EU aggregator first, then EU members; Switzerland and the UK are European
    // but not EU members, so they are appended manually.
    label: 'Europe',
    options: [
      ...EU_TAX_JURISDICTIONS,
      ...SWITZERLAND_TAX_JURISDICTIONS,
      { value: CountryCode.GB, label: 'United Kingdom' },
    ],
  },
  {
    label: 'Latin America',
    options: MEXICO_TAX_JURISDICTIONS,
  },
  {
    label: 'Middle East',
    options: [...SAUDI_ARABIA_TAX_JURISDICTIONS, ...UAE_TAX_JURISDICTIONS],
  },
  {
    label: 'Asia',
    options: [...SINGAPORE_TAX_JURISDICTIONS, ...INDIA_TAX_JURISDICTIONS],
  },
  {
    label: 'Oceania',
    options: [...AUSTRALIA_TAX_JURISDICTIONS, ...NEW_ZEALAND_TAX_JURISDICTIONS],
  },
];

// India ('IN') is included so new-registration-modal sets state_code='FD' for India registrations.
// isCountryLevelJurisdiction intentionally excludes 'IN' from this check to avoid collision
// with Indiana's US state code — it uses INDIA_JURISDICTION_FILTER_CODE ('ZZ_IN') instead.
export const FEDERAL_COUNTRIES = ['MX', 'AU', 'NZ', 'SA', 'SG', 'AE', 'CH', CountryCode.IN];
